"use client";

import { useTranslations } from "@/lib/compat/i18n";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { Monogram } from "@/components/persona/Monogram";
import { BrowserFrame } from "@/components/scene/BrowserFrame";
import { SellerTrustProfile } from "@/components/scenes/shared/SellerTrustProfile";
import { CompanyFacts, useAuroraFacts } from "@/components/scenes/shared/CompanyFacts";

/**
 * Screen 0.5 — Aurora's public Counterparty Portal page.
 *   orbitra.trade/c/aurora-aves/halal-whole-chicken
 *
 * Renders Aurora's Verified Trader badge prominently, her OrbID with LEI on
 * hover (Decision 20 — LEI lives inside the OrbID), the Seller Trust Profile,
 * and a Submit-a-Proposal CTA at the bottom.
 */
export function PublicCataloguePage({ className }: { className?: string }) {
  const t = useTranslations("publicCataloguePage");
  const tFacts = useTranslations("companyFacts");
  const auroraFacts = useAuroraFacts();
  return (
    <BrowserFrame url="orbitra.trade/c/aurora-aves/halal-whole-chicken?ref=almadar" className={className}>
      <div className="flex flex-col gap-6 p-6">
        {/* Personalisation banner */}
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-state-verified/40 bg-state-verified/10 px-4 py-3 text-[12px] text-orbitra-paper">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-state-verified">{t("personalisedFor")}</span>
          <span className="text-orbitra-teal-soft">·</span>
          <span>{t("deliveryPrefix")} <span className="text-orbitra-paper font-semibold">{t("deliveryLocation")}</span></span>
          <span className="text-orbitra-teal-soft">·</span>
          <span>{t("quotedTerms")}</span>
          <span className="ms-auto rounded-full border border-state-verified/30 bg-orbitra-ink/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-state-verified">
            {t("tokenisedLinkChip")}
          </span>
        </div>

        <div className="flex items-center gap-4">
          <Monogram name="Aurora Aves Brasil" flag="BR" size={48} />
          <div className="flex flex-col">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("corpOrbidKicker")}</p>
            <p className="font-display text-xl font-semibold text-orbitra-paper">{t("auroraAvesBrasilSA")}</p>
            <p className="font-mono text-[11px] text-orbitra-muted" title={t("orbidTooltip")}>
              corbid:5493-aves-aurora-br · BR
            </p>
          </div>
          <span className="ms-auto rounded-full border border-state-verified/40 bg-state-verified/10 px-3 py-1 font-display text-[11px] font-semibold uppercase tracking-wider text-state-verified shadow-[0_0_18px_var(--state-verified-glow)]">
            {t("verifiedTraderBadge")}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <SellerTrustProfile
            stats={{
              verifiedTrades: 24,
              lifetimeVolumeUsd: 14_800_000,
              onTimePct: 88,
              disputeCount: 0,
              coldChainIntegrityPct: 96,
              primarySubCorridor: t("corridorLabel"),
            }}
          />
          <CompanyFacts title={tFacts("title")} facts={auroraFacts} />
        </div>

        <div className="glass p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("listingKicker")}</p>
          <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{t("listingTitle")}</h3>
          <p className="mt-1 text-[12px] text-orbitra-teal-soft">
            {t("listingSpecsUsd")}
            <span className="text-orbitra-muted"> {t("listingSpecsAed")}</span>
            {" · "}{t("listingSpecsSuffix")}
          </p>
          <p className="mt-1 text-[11px] text-orbitra-teal-soft">
            <span className="font-mono text-state-verified">{t("incotermsChip")}</span>
            {" · "}{t("listingLogistics")}
          </p>

          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { src: "/catalogue/aurora-aves/packaged-product.png",    alt: t("photoAltPackaged") },
              { src: "/catalogue/aurora-aves/processing-facility.png", alt: t("photoAltFacility") },
              { src: "/catalogue/aurora-aves/cold-chain-diagram.png",  alt: t("photoAltColdChain") },
            ].map((p) => (
              <div key={p.src} className="aspect-[4/3] overflow-hidden rounded-md border border-state-verified/20 bg-orbitra-ink/40">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={p.src} alt={p.alt} className="h-full w-full object-cover" />
              </div>
            ))}
          </div>

          <div className="mt-5 flex items-center gap-3 rounded-md border border-state-verified/30 bg-orbitra-ink/40 px-3 py-2 text-[11px]">
            <span className="font-mono text-state-verified">●</span>
            <span className="text-orbitra-paper">{t("verifiedOnOrbitra")}</span>
            <span className="text-orbitra-muted">·</span>
            <span
              className="font-mono text-orbitra-teal-soft cursor-help"
              title={t("ledgerSeqTooltip")}
            >
              {t("ledgerSeq")}
            </span>
            <span className="ms-auto cursor-pointer text-state-verified underline-offset-2 hover:underline">{t("inspectProof")}</span>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-md border border-state-verified/40 bg-state-verified/10 px-5 py-4">
          <p className="text-[13px] text-orbitra-paper">
            {t("readyPrompt")}
          </p>
          <button type="button" onClick={advanceScreen} className="cta-primary px-4 py-2 text-[12px]">
            {t("submitProposalCta")}
          </button>
        </div>
      </div>
    </BrowserFrame>
  );
}
